// src/BoomboxButton.js
import React, { useEffect, useRef, useState } from "react";
import boombox from "./assets/boombox.png";
import boomboxLight from "./assets/boomboxlight.png";

const YT_ID = "Z6piLpMdIYQ";

function loadYT() {
  return new Promise((resolve) => {
    if (window.YT?.Player) return resolve(window.YT);
    const sid = "yt-iframe-api";
    if (!document.getElementById(sid)) {
      const s = document.createElement("script");
      s.id = sid;
      s.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(s);
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve(window.YT);
    };
  });
}

export default function BoomboxButton({ className = "boombox-fixed", style }) {
  const mountId = useRef(`yt-audio-${Math.random().toString(36).slice(2)}`);
  const playerRef = useRef(null);

  const [ready, setReady]  = useState(false);
  const [playing, setPlay] = useState(false);
  const [hover, setHover]  = useState(false);

  // Detect real hover (desktop) vs touch (mobile)
  const canHoverRef = useRef(false);
  useEffect(() => {
    const mq = window.matchMedia?.("(hover: hover) and (pointer: fine)");
    const update = () => {
      canHoverRef.current = !!mq?.matches;
      setHover(false); // clear any sticky hover when switching
    };
    update();
    if (mq?.addEventListener) mq.addEventListener("change", update);
    else if (mq?.addListener) mq.addListener(update);
    return () => {
      if (mq?.removeEventListener) mq.removeEventListener("change", update);
      else if (mq?.removeListener) mq.removeListener(update);
    };
  }, []);

  useEffect(() => {
    let destroyed = false;
    loadYT().then((YT) => {
      if (destroyed) return;
      const p = new YT.Player(mountId.current, {
        width: 1,
        height: 1,
        videoId: YT_ID,
        playerVars: {
          autoplay: 0,
          controls: 0,
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          loop: 1,
          playlist: YT_ID,
          origin: window.location.origin
        },
        events: {
          onReady: () => setReady(true),
          onStateChange: (e) => {
            const S = YT.PlayerState;
            setPlay(e.data === S.PLAYING);
          }
        }
      });
      playerRef.current = p;
    });
    return () => {
      destroyed = true;
      try { playerRef.current?.destroy(); } catch {}
    };
  }, []);

  const toggle = () => {
    const p = playerRef.current;
    if (!ready || !p) return;
    try { p.unMute?.(); } catch {}
    const state = p.getPlayerState?.();
    if (state === 1) p.pauseVideo();
    else p.playVideo();
  };

  // Only apply hover light on devices that truly hover
  const showLight = playing || (canHoverRef.current && hover);

  // Conditionally attach mouse handlers (not on touch devices)
  const hoverHandlers = canHoverRef.current
    ? {
        onMouseEnter: () => setHover(true),
        onMouseLeave: () => setHover(false),
      }
    : {};

    // src/BoomboxButton.js
    return (
      <div className={className} style={style}>
        <button
          className={`boombox-btn ${playing ? "on" : ""}`}
          type="button"
          aria-pressed={playing}
          aria-label={playing ? "Pause music" : "Play music"}
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
          }}
          {...hoverHandlers}
        >
          <img src={showLight ? boomboxLight : boombox} alt="" draggable="false" />
        </button>

        {/* tiny YouTube mount */}
        <div id={mountId.current} className="yt-audio-tiny" aria-hidden="true" />
      </div>
    );

    
}
